import { Injectable } from '@angular/core';
import type { Workbook, Worksheet } from 'exceljs';
import { TableColumn } from '../_models/exportTable.vm';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

export enum ExportType {
  CSV,
  XLSX
}

/** One worksheet in a multi-sheet workbook. */
export interface ExportSheet {
  /** Worksheet name. Trimmed to what Excel accepts before it is used. */
  name: string;
  columns: Array<TableColumn>;
  data: Array<any>;
}

@Injectable({
  providedIn: 'root'
})
export class ExportTableService {

  constructor(private http: HttpClient) { }

  // ExcelJS and file-saver are only needed the moment someone exports, and ExcelJS
  // is by far the largest dependency in the app, so both are pulled in on demand
  // rather than shipped in the initial bundle. `import type` above is erased at
  // compile time and adds nothing to the build.
  private async loadExcelJs(): Promise<typeof import('exceljs')> {
    const module: any = await import('exceljs');
    return module.default ?? module;
  }

  private async saveBuffer(buffer: ArrayBuffer, filename: string): Promise<void> {
    const { saveAs } = await import('file-saver');
    saveAs(new Blob([buffer]), filename);
  }

  async exportDataToCSVOrXLSX(
    data: Array<any>,
    columns: Array<TableColumn>,
    filename: string,
    type: ExportType,
    section?: string
  ): Promise<void> {
    const ExcelJS = await this.loadExcelJs();
    const wb: Workbook = new ExcelJS.Workbook();
    const ws: Worksheet = wb.addWorksheet('Sheet 1');

    ws.columns = columns.map((column) => {
      return {
        header: column.name,
        key: column.field,
        width: 20
      };
    });

    data.forEach((row) => {
      const values: Array<any> = [];
      columns.forEach((column) => {
        values.push(row[column.field]);
      });
      ws.addRow(values);
    });

    ws.getRow(1).eachCell((cell) => {
      cell.font = { bold: true };
    });

    switch (type) {
      case ExportType.CSV:
        const csv_buffer = await wb.csv.writeBuffer();
        await this.saveBuffer(csv_buffer as ArrayBuffer, `${filename}.csv`);
        break;

      case ExportType.XLSX:
        if (section != 'extract table') {
          const xlsx_buffer = await wb.xlsx.writeBuffer();
          await this.saveBuffer(xlsx_buffer as ArrayBuffer, `${filename}.xlsx`);
        }
        if (section === 'extract table') {
          this.http.get('assets/documents/CVDP_coversheet.xlsx', { responseType: 'arraybuffer' }).subscribe(async (coverArrayBuffer: ArrayBuffer) => {
          const coverWorkbook = new ExcelJS.Workbook();
          await coverWorkbook.xlsx.load(coverArrayBuffer);

          // Add a new worksheet to the cover workbook
          const coverWorksheet = coverWorkbook.addWorksheet();

          // Copy contents from ws to coverWorksheet
          ws.eachRow((row, rowNumber) => {
          const newRow = coverWorksheet.addRow([]);
          row.eachCell((cell, colNumber) => {
            newRow.getCell(colNumber).value = cell.value;
            });
          });

          // Write the updated cover workbook back to a buffer
          const updatedBuffer = await coverWorkbook.xlsx.writeBuffer();

          // Save the buffer as a file
          await this.saveBuffer(updatedBuffer as ArrayBuffer, `${filename}.xlsx`);
        });
      }
      break;
    }
  }

  /**
   * Writes several tables into one .xlsx file, a worksheet each, behind the same
   * cover page the single extract download uses. The data extract page offers this
   * so a comparison of up to three extracts arrives as one workbook rather than as
   * three separate downloads.
   */
  async exportSheetsToXLSX(sheets: Array<ExportSheet>, filename: string): Promise<void> {
    if (!sheets || !sheets.length) {
      return;
    }

    const ExcelJS = await this.loadExcelJs();
    const wb: Workbook = new ExcelJS.Workbook();

    // The cover page is a prepared workbook the extracts are appended to. Without
    // it the download would lose the explanatory front sheet the single download has.
    const coverBuffer = await firstValueFrom(
      this.http.get('assets/documents/CVDP_coversheet.xlsx', { responseType: 'arraybuffer' })
    );
    await wb.xlsx.load(coverBuffer as ArrayBuffer);

    const used = new Set<string>(wb.worksheets.map(sheet => sheet.name));

    sheets.forEach((sheet) => {
      const ws: Worksheet = wb.addWorksheet(this.uniqueSheetName(sheet.name, used));

      ws.columns = sheet.columns.map(column => ({ header: column.name, key: column.field, width: 20 }));

      sheet.data.forEach((row) => {
        ws.addRow(sheet.columns.map(column => row[column.field]));
      });

      ws.getRow(1).eachCell((cell) => {
        cell.font = { bold: true };
      });
    });

    const buffer = await wb.xlsx.writeBuffer();
    await this.saveBuffer(buffer as ArrayBuffer, `${filename}.xlsx`);
  }

  /**
   * Excel rejects a worksheet name over 31 characters, one containing : \ / ? * [ ]
   * and one that repeats a name already in the workbook, and throws rather than
   * correcting it, so every name is put through this first.
   */
  private uniqueSheetName(name: string, used: Set<string>): string {
    const base = (name || 'Extract').replace(/[:\\/?*\[\]]/g, ' ').trim().slice(0, 31) || 'Extract';
    let candidate = base;
    let suffix = 2;
    while (used.has(candidate)) {
      const tail = ` (${suffix++})`;
      candidate = base.slice(0, 31 - tail.length) + tail;
    }
    used.add(candidate);
    return candidate;
  }

  /**
   * Writes a rendered HTML table out as an .xlsx file.
   *
   * This replaces SheetJS's `table_to_sheet` + `writeFile`. SheetJS's npm build is
   * abandoned and carries unfixable advisories, and the app already ships ExcelJS,
   * so the two export paths now share one library. Row and column spans are
   * expanded into real merged ranges so the output still matches what is on screen.
   */
  async exportHtmlTable(table: HTMLElement, sheetName: string, filename: string): Promise<void> {
    if (!table) {
      return;
    }

    const ExcelJS = await this.loadExcelJs();
    const wb: Workbook = new ExcelJS.Workbook();
    // Excel rejects sheet names over 31 characters or containing : \ / ? * [ ]
    const safeSheetName = (sheetName || 'Sheet 1').replace(/[:\\/?*\[\]]/g, ' ').trim().slice(0, 31) || 'Sheet 1';
    const ws: Worksheet = wb.addWorksheet(safeSheetName);

    const rows = Array.from(table.querySelectorAll('tr'));
    // Cells covered by an earlier cell's rowspan/colspan, so the next cell in the
    // row is placed in the first column that is actually free rather than shifting
    // everything left of where it appears on screen.
    const covered = new Set<string>();
    const merges: Array<[number, number, number, number]> = [];
    let widestColumn = 0;

    rows.forEach((tr, rowIndex) => {
      let columnIndex = 0;

      Array.from(tr.children).forEach((child) => {
        const cell = child as HTMLTableCellElement;
        while (covered.has(`${rowIndex},${columnIndex}`)) {
          columnIndex++;
        }

        const rowSpan = cell.rowSpan || 1;
        const colSpan = cell.colSpan || 1;
        const text = (cell.textContent || '').replace(/\s+/g, ' ').trim();

        ws.getRow(rowIndex + 1).getCell(columnIndex + 1).value = this.coerceCellValue(text);

        for (let r = 0; r < rowSpan; r++) {
          for (let c = 0; c < colSpan; c++) {
            if (r !== 0 || c !== 0) {
              covered.add(`${rowIndex + r},${columnIndex + c}`);
            }
          }
        }

        if (rowSpan > 1 || colSpan > 1) {
          merges.push([rowIndex + 1, columnIndex + 1, rowIndex + rowSpan, columnIndex + colSpan]);
        }

        columnIndex += colSpan;
        widestColumn = Math.max(widestColumn, columnIndex);
      });
    });

    merges.forEach(([top, left, bottom, right]) => {
      try {
        ws.mergeCells(top, left, bottom, right);
      } catch {
        // Overlapping spans in the source markup are not worth failing an export over.
      }
    });

    for (let c = 1; c <= widestColumn; c++) {
      ws.getColumn(c).width = 20;
    }
    if (rows.length) {
      ws.getRow(1).eachCell((cell) => {
        cell.font = { bold: true };
      });
    }

    const buffer = await wb.xlsx.writeBuffer();
    await this.saveBuffer(buffer as ArrayBuffer, filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`);
  }

  /**
   * Keeps plain numbers numeric so the spreadsheet can still sum and chart them.
   * Anything carrying a unit, including percentages, is left exactly as it reads on
   * screen: turning "45.2%" into a bare 45.2 (or into 0.452, as SheetJS did) changes
   * what the figure means, which is not a trade worth making on audit data.
   */
  private coerceCellValue(text: string): string | number {
    if (text === '' || !/^-?[\d,]+(\.\d+)?$/.test(text)) {
      return text;
    }
    const numeric = Number(text.replace(/,/g, ''));
    return Number.isNaN(numeric) ? text : numeric;
  }
}
